import type { LegalDocument } from '../legal-content';

/**
 * Renders one approved legal document (PP-029 / proweb-102 AC-001, AC-003).
 *
 * While no approved text has been supplied the page says so plainly and points at the authoritative
 * copy, rather than showing an empty page or an approximation of a legal document. See
 * `legal-content.ts` for where the text goes.
 */
export function LegalDocumentPage({ document }: { document: LegalDocument }): React.JSX.Element {
  const headingId = `${document.title.replace(/\W+/g, '-').toLowerCase()}-heading`;

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-5">
      <header>
        <h1 id={headingId} className="text-brand-600 text-xl font-bold">
          {document.title}
        </h1>
        {document.revision !== null && (
          <p className="text-muted mt-1 text-sm">Last revised {document.revision}.</p>
        )}
      </header>

      <div className="border-line rounded-xl border bg-surface p-6 shadow-sm">
        {document.sections.length === 0 ? (
          <PendingApproval document={document} />
        ) : (
          <div className="flex flex-col gap-6">
            {document.sections.map((section) => (
              <section key={section.heading}>
                <h2 className="text-brand-600 text-base font-bold">{section.heading}</h2>
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph.slice(0, 60)} className="text-ink mt-2 text-sm leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </section>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function PendingApproval({ document }: { document: LegalDocument }): React.JSX.Element {
  return (
    <div className="text-sm">
      <p className="text-muted">
        The approved {document.title} is not yet published in the portal. The current version is
        available on the Bankers Fidelity website.
      </p>
      <p className="mt-3">
        <a
          href={document.sourceUrl}
          target="_blank"
          rel="noreferrer"
          className="text-brand-500 underline"
        >
          Read the {document.title}
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
      </p>
    </div>
  );
}
